import { LinkOutlined } from '@ant-design/icons';
import type { CSSProperties, ReactNode } from 'react';
import type { StatusCategoryKey } from '../theme/tokens.ts';

/**
 * Единый чип значения-сущности для грида/карточки/канбана.
 *  - tone="status"    — статусная категория: мягкая подложка + точка по категории;
 *  - tone="select"    — обычная опция select: тон из option.color;
 *  - tone="reference" — ссылка на справочник: белый чип с иконкой-звеном и
 *                       человекочитаемым значением (displayColumn), не id.
 * Заменяет разнородные локальные теги. В срез 2 на него переходят
 * renderFieldValue и канбан.
 */

interface ChipProps {
  label: ReactNode;
  tone?: 'status' | 'select' | 'reference';
  /** Для tone="status". */
  category?: StatusCategoryKey;
  /** Для tone="select": цвет опции (option.color). */
  color?: string | null;
  style?: CSSProperties;
}

const base: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 22,
  padding: '0 9px',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1,
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
};

function dot(bg: string): JSX.Element {
  return <span style={{ width: 6, height: 6, borderRadius: '50%', background: bg, flex: 'none' }} />;
}

/**
 * text-overflow не работает на flex-контейнере (текст — анонимный flex-item),
 * поэтому многоточие вешаем на внутренний span, а не на сам чип.
 */
function clampedLabel(label: ReactNode): JSX.Element {
  return (
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

export function Chip({ label, tone = 'select', category, color, style }: ChipProps): JSX.Element {
  // Полное значение по наведению — метка может быть обрезана многоточием.
  const title = typeof label === 'string' ? label : undefined;
  if (tone === 'status' && category) {
    return (
      <span
        className="lens-chip"
        title={title}
        style={{
          ...base,
          background: `var(--status-${category}-bg)`,
          color: `var(--status-${category}-fg)`,
          ...style,
        }}
      >
        {dot(`var(--status-${category}-dot)`)}
        {clampedLabel(label)}
      </span>
    );
  }
  if (tone === 'reference') {
    return (
      <span
        className="lens-chip"
        title={title}
        style={{
          ...base,
          background: 'var(--color-bg-container, #fff)',
          border: '1px solid var(--color-border, #e2e1ea)',
          color: 'var(--color-text, #2a2733)',
          ...style,
        }}
      >
        <LinkOutlined style={{ fontSize: 11, color: 'var(--color-text-secondary, #9a97a6)', flex: 'none' }} aria-hidden />
        {clampedLabel(label)}
      </span>
    );
  }
  // select — точку-маркер показываем ТОЛЬКО у опций с явным цветом (option.color).
  // У бесцветных select'ов (напр. «Отчётный период») серая точка — визуальный шум:
  // это не статус, категории у поля нет. Тогда чип = просто тонированный текст.
  const tint = color ?? '#8c8c8c';
  return (
    <span className="lens-chip" title={title} style={{ ...base, background: `${tint}1f`, color: tint, ...style }}>
      {color ? dot(tint) : null}
      {clampedLabel(label)}
    </span>
  );
}
